import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { BaseVideo, useOnboarding } from '../../../context/OnboardingContext'
import { useRemainingTime } from '../hooks/useRemainingTime'
import { CheckCircle, Circle } from 'lucide-react'
import Logger from '../../../helpers/logger'
import { useLanguages } from '../../../hooks/useLanguages'
import { useTranslation } from 'react-i18next'
import React from 'react'

const ProgressBlock = ({ currentVideoId, isCollapsed }: Props) => {
    const { getAllVideos } = useOnboarding()
    const { remainingTime } = useRemainingTime()
    const { t } = useTranslation('onboarding')
    const { userLang } = useLanguages()
    const { resolvedLanguage } = userLang

    const includeDecisionsRef = useRef<{
        includeComplementary: boolean
        includeRecommended: boolean
        initialTime: number
    } | null>(null)
    const prevVideoIdRef = useRef<string | null>(null)
    const [availableVideos, setAvailableVideos] = useState<BaseVideo[]>([])

    useEffect(() => {
        // Only calculate if we have a valid remaining time
        if (remainingTime === null) {
            Logger.debug('ProgressBlock', '[INIT] Waiting for valid remaining time')
            return
        }

        const calculateAvailableVideos = () => {
            const allVideos = getAllVideos()
            const totalTime = remainingTime

            const prePhaseVideos = allVideos.filter(v => v.metadata.phase === 'pre')
            const mainPhaseVideos = allVideos.filter(v => v.metadata.phase === 'main')

            const mandatoryVideos = mainPhaseVideos.filter(v => v.type === 'mandatory')
            const icebreakers = mainPhaseVideos.filter(v => v.metadata.category === 'icebreaker')
            const complementaryVideos = mainPhaseVideos.filter(
                v => v.type === 'complementary' && v.metadata.category !== 'icebreaker'
            )
            const recommendedVideos = mainPhaseVideos.filter(
                v => v.type === 'recommended' && v.metadata.category !== 'icebreaker'
            )

            const mandatoryTime = mandatoryVideos.filter(v => !v.completed).reduce((sum, v) => sum + v.length, 0)
            const complementaryTime = complementaryVideos
                .filter(v => !v.completed)
                .reduce((sum, v) => sum + v.length, 0)
            const recommendedTime = recommendedVideos.filter(v => !v.completed).reduce((sum, v) => sum + v.length, 0)

            const timeAfterMandatory = totalTime - mandatoryTime

            // Logger.debug('ProgressBlock', '[TIME] Time calculations:', {
            //     totalTime,
            //     mandatoryTime,
            //     complementaryTime,
            //     recommendedTime,
            //     timeAfterMandatory,
            //     includeDecisions: includeDecisionsRef.current,
            //     currentVideoId,
            // })

            // Make initial decision only when we have a valid time
            if (!includeDecisionsRef.current) {
                includeDecisionsRef.current = {
                    includeComplementary: timeAfterMandatory >= complementaryTime,
                    includeRecommended: timeAfterMandatory >= complementaryTime + recommendedTime,
                    initialTime: totalTime,
                }

                // Logger.debug('ProgressBlock', '[DECISION] Initial decisions made:', {
                //     ...includeDecisionsRef.current,
                //     timeAfterMandatory,
                //     complementaryTime,
                //     mandatoryTime,
                // })
            }

            // Start with mandatory videos
            let availableVideos = [...prePhaseVideos, ...mandatoryVideos]

            // Use stored decisions
            if (includeDecisionsRef.current.includeComplementary) {
                Logger.debug('ProgressBlock', '[VIDEOS] Including complementary based on initial decision:', {
                    timeAfterMandatory,
                    complementaryTime,
                    initialTimeWas: includeDecisionsRef.current.initialTime,
                })
                availableVideos = [...availableVideos, ...complementaryVideos]

                if (includeDecisionsRef.current.includeRecommended) {
                    Logger.debug('ProgressBlock', '[VIDEOS] Including recommended based on initial decision')
                    availableVideos = [...availableVideos, ...recommendedVideos]
                }
            }

            // Always add icebreakers last
            availableVideos = [...availableVideos, ...icebreakers]

            // Reset decisions if video changes
            if (currentVideoId !== prevVideoIdRef.current) {
                Logger.debug('ProgressBlock', '[VIDEO_CHANGE] Video changed, resetting decisions:', {
                    from: prevVideoIdRef.current,
                    to: currentVideoId,
                })
                includeDecisionsRef.current = null
                prevVideoIdRef.current = currentVideoId ?? null
            }

            // Logger.debug('ProgressBlock', '[FINAL] Video list:', {
            //     totalVideos: allVideos.length,
            //     availableCount: availableVideos.length,
            //     currentVideoId,
            //     includeDecisions: includeDecisionsRef.current,
            //     availableVideoIds: availableVideos.map(v => v.id),
            //     types: availableVideos.map(v => v.type),
            // })

            return availableVideos.sort((a, b) => {
                if (a.metadata.phase === 'pre' && b.metadata.phase !== 'pre') return -1
                if (a.metadata.phase !== 'pre' && b.metadata.phase === 'pre') return 1
                if (a.metadata.category === 'icebreaker') return 1
                if (b.metadata.category === 'icebreaker') return -1
                return a.order - b.order
            })
        }

        setAvailableVideos(calculateAvailableVideos())
    }, [remainingTime, currentVideoId, getAllVideos])

    const formatDuration = (seconds: number): string => {
        if (seconds < 60) {
            return `${seconds} seconds`
        }

        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }

    if (!availableVideos.length) return null

    return (
        <Container isCollapsed={isCollapsed}>
            <ProgressContainer isCollapsed={isCollapsed}>
                <Title isCollapsed={isCollapsed}>{t('progress.progressTitle')}</Title>
                <StepsList isCollapsed={isCollapsed}>
                    {availableVideos.map(video => {
                        const isCompleted = video.completed
                        const isCurrent = video.id === currentVideoId

                        return (
                            <StepItem key={video.id} isCurrent={isCurrent} isCompleted={isCompleted}>
                                <StepIconWrapper>
                                    {isCompleted ? (
                                        <CheckCircle size={20} className="text-blue-500" />
                                    ) : (
                                        <Circle size={20} />
                                    )}
                                </StepIconWrapper>
                                <StepContent>
                                    <StepTitle isCurrent={isCurrent}>
                                        {typeof video.title === 'string'
                                            ? video.title
                                            : resolvedLanguage && video.title.lang[resolvedLanguage]
                                            ? video.title.lang[resolvedLanguage]
                                            : video.title.lang.en || ''}
                                    </StepTitle>
                                    <StepDuration>{formatDuration(video.length)}</StepDuration>
                                </StepContent>
                            </StepItem>
                        )
                    })}
                </StepsList>
            </ProgressContainer>
        </Container>
    )
}

export default React.memo(ProgressBlock)

type Props = {
    currentVideoId?: string
    isCollapsed: boolean
}

const Container = styled.div<{ isCollapsed: boolean }>`
    transition: width 0.3s ease;
    width: ${props => (props.isCollapsed ? '0' : '280px')};
    overflow: hidden;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        width: ${props => (props.isCollapsed ? '0' : '240px')};
    }

    @media (max-width: 768px) {
        width: 100%;
        height: auto;
    }
`

const ProgressContainer = styled.div<{ isCollapsed: boolean }>`
    width: 100%;
    height: 100%;
    padding: ${props => (props.isCollapsed ? '1.5rem 0.75rem' : '1.5rem 2rem')};
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    overflow-y: auto;
    position: sticky;
    top: 0;

    /* Hide scrollbar */
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    @media (max-width: 1024px) {
        padding: ${props => (props.isCollapsed ? '1.5rem 0.75rem' : '1.5rem')};
    }

    @media (max-width: 768px) {
        position: relative;
        height: auto;
        border-left: none;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        margin-top: 2rem;
        padding: 1.5rem;
    }
`

const Title = styled.h3<{ isCollapsed: boolean }>`
    font-size: 0.875rem;
    color: #94a3b8;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    opacity: ${props => (props.isCollapsed ? 0 : 1)};
    transition: opacity 0.2s ease;
    height: ${props => (props.isCollapsed ? '0' : 'auto')};
    margin: ${props => (props.isCollapsed ? '0' : '0 0 1.5rem 0')};
`

const StepsList = styled.div<{ isCollapsed: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    opacity: ${props => (props.isCollapsed ? 0 : 1)};
    transition: opacity 0.2s ease;
    visibility: ${props => (props.isCollapsed ? 'hidden' : 'visible')};

    @media (max-width: 768px) {
        gap: 1rem;
        opacity: 1;
        visibility: visible;
    }
`

const StepItem = styled.div<{ isCurrent?: boolean; isCompleted?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 6px;
    transition: all 0.2s ease;
    opacity: ${props => (props.isCompleted ? 0.6 : 1)};

    ${props =>
        props.isCurrent &&
        `
        background: rgba(59, 130, 246, 0.1);
        box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
    `}

    @media (max-width: 768px) {
        padding: 0.5rem;
    }
`

const StepIconWrapper = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;

    @media (max-width: 768px) {
        width: 20px;
        height: 20px;
    }
`

const StepContent = styled.div`
    flex: 1;
    min-width: 0;
`

const StepTitle = styled.div<{ isCurrent?: boolean }>`
    font-size: 0.9375rem;
    color: ${props => (props.isCurrent ? '#E2E8F0' : '#94A3B8')};
    font-weight: ${props => (props.isCurrent ? '600' : '500')};
    transition: color 0.2s ease;
    line-height: 1.4;

    @media (max-width: 768px) {
        font-size: 0.875rem;
    }
`

const StepDuration = styled.div`
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.25rem;
    font-weight: 500;
`
